import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Dropdown, type MenuProps, Tooltip } from 'antd';
import { useScheduleBoardContext } from 'pos-client/components/scheduleBoard/context/ScheduleBoardContext.tsx';
import { usePermissions } from 'pos-client/hooks/usePermissions.tsx';
import type { ScheduleBoardJobModel } from 'pos-shared/models/schedulingSchema.ts';

interface WorkshopJobActionsProps {
  job: ScheduleBoardJobModel;
}

export const WorkshopJobActions = ({ job }: WorkshopJobActionsProps) => {
  const { t } = useTranslation();
  const { hasPermissions } = usePermissions();
  const canRescheduleJob = hasPermissions('reschedule.job');

  const { setJobDetailsModalData, setSelectedOrderDetailsForAssignTechnician } =
    useScheduleBoardContext();

  const jobCompletedCancelled =
    job.state === 'complete' || job.state === 'cancelled';
  const jobRescheduled = job.state.includes('rescheduled');

  const onViewJobDetails = useCallback(() => {
    setJobDetailsModalData(job);
    setSelectedOrderDetailsForAssignTechnician({
      orderId: job.orderId,
      jobId: job.jobId,
    });
  }, [job, setJobDetailsModalData, setSelectedOrderDetailsForAssignTechnician]);

  const menuItems: MenuProps['items'] = useMemo(
    () => [
      {
        label: t('jobs.jobDetails'),
        key: 'job-details',
        onClick: onViewJobDetails,
      },
      {
        label: (
          <Tooltip
            title={
              jobCompletedCancelled
                ? t('common.states.completedOrCancelled')
                : ''
            }
          >
            <span>{t('scheduling.reschedule')}</span>
          </Tooltip>
        ),
        key: 'reschedule',
        onClick: () =>
          window.open(
            `/orders/manage-orders/${job.orderId}/reschedule/${job.bookingReference}/${job.scheduleId}`,
            '_blank'
          ),
        disabled: jobCompletedCancelled || jobRescheduled,
        hidden: !canRescheduleJob,
      },
    ],
    [
      t,
      onViewJobDetails,
      jobCompletedCancelled,
      jobRescheduled,
      canRescheduleJob,
      job.orderId,
      job.bookingReference,
      job.scheduleId,
    ]
  );

  return (
    <>
      <Dropdown
        menu={{ items: menuItems }}
        disabled={job.state.includes('blocked')}
      >
        <Button size="small" icon={<FontAwesomeIcon icon={faChevronDown} />}>
          {t('common.buttons.actions')}
        </Button>
      </Dropdown>
    </>
  );
};
